import * as THREE from 'three'

import Area from './Area.js'

export default class Areas
{
    constructor(_options)
    {
        // Options
        this.config = _options.config
        this.resources = _options.resources
        this.car = _options.car
        this.sounds = _options.sounds
        this.renderer = _options.renderer
        this.camera = _options.camera
        this.time = _options.time
        this.debug = _options.debug

        // Set up
        this.items = []
        this.container = new THREE.Object3D()
        this.container.matrixAutoUpdate = false

        this.setMouse()
    }

    setMouse()
    {
        // Set up
        this.mouse = {}
        this.mouse.raycaster = new THREE.Raycaster()
        this.mouse.coordinates = new THREE.Vector2()
        this.mouse.currentArea = null
        this.mouse.needsUpdate = false

        // Mouse move event
        window.addEventListener('mousemove', (_event) =>
        {
            this.setPointerCoordinates(_event.clientX, _event.clientY)

            this.mouse.needsUpdate = true
        })

        // Mouse click event
        this.mouse.suppressClickUntil = 0

        window.addEventListener('mousedown', () =>
        {
            if(performance.now() < this.mouse.suppressClickUntil)
            {
                return
            }

            if(this.mouse.currentArea)
            {
                this.mouse.currentArea.interact(false)
            }
        })

        // Tap a link while the car is parked on it
        this.mouse.tap = null

        this.renderer.domElement.addEventListener('touchstart', (_event) =>
        {
            if(_event.touches.length !== 1)
            {
                this.mouse.tap = null
                return
            }

            const touch = _event.changedTouches[0]

            this.mouse.tap = {
                x: touch.clientX,
                y: touch.clientY,
                identifier: touch.identifier
            }
        }, { passive: true })

        this.renderer.domElement.addEventListener('touchend', (_event) =>
        {
            this.finishTap(_event)
        })

        this.renderer.domElement.addEventListener('touchcancel', (_event) =>
        {
            this.finishTap(_event)
        })

        // Time tick event
        this.time.on('tick', () =>
        {
            // Only update if needed
            if(this.mouse.needsUpdate)
            {
                this.mouse.needsUpdate = false

                // Set up
                this.mouse.raycaster.setFromCamera(this.mouse.coordinates, this.camera.instance)
                const objects = this.items.map((_area) => _area.mouseMesh)
                const intersects = this.mouse.raycaster.intersectObjects(objects)

                // Intersections found
                if(intersects.length)
                {
                    // Find the area
                    const area = this.items.find((_area) => _area.mouseMesh === intersects[0].object)

                    // Area did change
                    if(area !== this.mouse.currentArea)
                    {
                        // Was previously over an area
                        if(this.mouse.currentArea !== null)
                        {
                            // Play out
                            this.mouse.currentArea.out()
                            this.mouse.currentArea.testCar = this.mouse.currentArea.initialTestCar
                        }

                        // Play in
                        this.mouse.currentArea = area
                        this.mouse.currentArea.in(false)
                        this.mouse.currentArea.testCar = false
                    }
                }
                // No intersections found but was previously over an area
                else if(this.mouse.currentArea !== null)
                {
                    // Play out
                    this.mouse.currentArea.out()
                    this.mouse.currentArea.testCar = this.mouse.currentArea.initialTestCar
                    this.mouse.currentArea = null
                }
            }
        })
    }

    setPointerCoordinates(_x, _y)
    {
        const bounds = this.renderer.domElement.getBoundingClientRect()
        const width = bounds.width || window.innerWidth
        const height = bounds.height || window.innerHeight

        this.mouse.coordinates.x = ((_x - bounds.left) / width) * 2 - 1
        this.mouse.coordinates.y = - ((_y - bounds.top) / height) * 2 + 1
    }

    finishTap(_event)
    {
        const start = this.mouse.tap
        this.mouse.tap = null

        if(!start)
        {
            return
        }

        const touch = [..._event.changedTouches].find((_touch) => _touch.identifier === start.identifier)
        const x = touch ? touch.clientX : start.x
        const y = touch ? touch.clientY : start.y

        this.mouse.suppressClickUntil = performance.now() + 700

        if(Math.hypot(x - start.x, y - start.y) > 22)
        {
            return
        }

        const area = this.areaUnderTap(x, y)

        if(area)
        {
            area.interact(false)
        }
    }

    areaUnderTap(_x, _y)
    {
        const containsCar = (_area) =>
        {
            if(!_area.active || !_area.car)
            {
                return false
            }

            return Math.abs(_area.car.position.x - _area.position.x) < Math.abs(_area.halfExtents.x) && Math.abs(_area.car.position.y - _area.position.y) < Math.abs(_area.halfExtents.y)
        }

        const hasInteract = (_area) =>
        {
            const callbacks = _area.callbacks || {}

            for(const namespace in callbacks)
            {
                const interact = callbacks[namespace] && callbacks[namespace].interact

                if(interact && interact.length)
                {
                    return true
                }
            }

            return false
        }

        const parked = this.items.filter((_area) => containsCar(_area) && hasInteract(_area))
        const buttons = this.items.filter((_area) => _area.active && _area.testCar === false && hasInteract(_area))

        if(!parked.length && !buttons.length)
        {
            return null
        }

        const smallest = (_areas) =>
        {
            return _areas.slice().sort((_a, _b) => Math.abs(_a.halfExtents.x * _a.halfExtents.y) - Math.abs(_b.halfExtents.x * _b.halfExtents.y))[0]
        }

        this.setPointerCoordinates(_x, _y)
        this.mouse.raycaster.setFromCamera(this.mouse.coordinates, this.camera.instance)

        if(buttons.length)
        {
            const buttonHits = this.mouse.raycaster.intersectObjects(buttons.map((_area) => _area.mouseMesh))
                .map((_intersect) => buttons.find((_area) => _area.mouseMesh === _intersect.object))
                .filter(Boolean)

            if(buttonHits.length)
            {
                return smallest(buttonHits)
            }
        }

        if(!parked.length)
        {
            return null
        }

        const intersects = this.mouse.raycaster.intersectObjects(parked.map((_area) => _area.mouseMesh))

        if(intersects.length)
        {
            const hits = intersects
                .map((_intersect) => parked.find((_area) => _area.mouseMesh === _intersect.object))
                .filter(Boolean)

            if(hits.length)
            {
                return smallest(hits)
            }
        }

        return smallest(parked)
    }

    add(_options)
    {
        const area = new Area({
            config: this.config,
            renderer: this.renderer,
            resources: this.resources,
            car: this.car,
            sounds: this.sounds,
            time: this.time,
            hasKey: true,
            testCar: true,
            active: true,
            ..._options
        })

        this.container.add(area.container)

        this.items.push(area)

        return area
    }
}
